import type { TOC } from '@ember/component/template-only';
import {
  PhHouse,
  PhGraduationCap,
  PhFirstAidKit,
  PhLifebuoy,
  PhHandCoins,
  PhGlobe,
  PhMegaphone,
  PhBookOpenText,
  PhHeadset,
  PhQuestionMark,
  PhSun,
  PhHandshake,
  PhTrendUp,
  PhSpinnerGap,
  PhArrowRight,
  PhFileText,
  PhGift,
  PhShieldCheck,
  PhRocketLaunch,
  PhSealQuestion,
  PhTarget,
  PhBookOpen,
  PhScales,
} from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';

// ---------------------------------------------------------------------------
// Tio Home — Brand Updates 2026 (static design prototype)
//
// Standalone visual port of the Figma "Tio Home_active user" concept
// (Brand Updates 2026, node 5:10332). Renders full-viewport, independent of
// the surrounding app/reimagined chrome, with hardcoded data matching the
// design exactly — not wired to live services.
// ---------------------------------------------------------------------------

const NEEDS_ATTENTION = [
  {
    icon: PhGraduationCap,
    iconBg: 'bg-[#fae6e9]',
    title: 'Your Education Request Needs Attention',
    chipLabel: 'Act Now',
    chipBg: 'bg-[#fae6e9]',
    chipText: 'text-[#8f0b21]',
    description: 'Submit your evidence items to keep the process moving smoothly.',
    linkLabel: 'Submit evidence',
  },
  {
    icon: PhFileText,
    iconBg: 'bg-[#fef3da]',
    title: 'Annual PSLF Certification Due Soon',
    chipLabel: 'Due Soon',
    chipBg: 'bg-[#fef3da]',
    chipText: 'text-[#523901]',
    description: 'Certify your qualifying payments to stay on track for forgiveness',
    linkLabel: 'Certify now',
  },
];

const MORE_TO_EXPLORE = [
  {
    icon: PhShieldCheck,
    title: 'Loan Forgiveness (PSLF)',
    description: 'Qualifying employment can unlock your loan forgiveness.',
    tag: 'up to 100% forgiven',
  },
  {
    icon: PhGlobe,
    title: 'Learning Partner Network',
    description: 'You may be eligible for discounts at top institutions!',
    tag: 'up to 25% off',
  },
  {
    icon: PhHeadset,
    title: 'Education Mentorship',
    description: "We're here to help you maximize your benefits.",
    tag: 'make an appointment',
  },
  {
    icon: PhRocketLaunch,
    title: 'Secure Your Future',
    description: 'Qualifying loan payments can trigger a 401(k) match.',
    tag: '$800/year',
  },
];

const RESOURCES = [
  {
    icon: PhSealQuestion,
    title: 'FAQs',
    description: "Have a question? We likely have the answers you're looking for.",
  },
  {
    icon: PhTarget,
    title: 'Strategy Finder',
    description: 'Compare options and find the best plan for your situation.',
  },
  {
    icon: PhBookOpen,
    title: 'Knowledge Base',
    description: 'Learn more about the benefit programs available to you.',
  },
  {
    icon: PhScales,
    title: 'Income Driven Repayment',
    description: 'Balance your payments so you only pay what you can afford.',
  },
];

interface HomeTwentyTwentySixSignature {
  Element: HTMLDivElement;
}

const HomeTwentyTwentySix: TOC<HomeTwentyTwentySixSignature> = <template>
  <div class="fixed inset-0 z-[100] bg-[#0d0d0d] overflow-y-auto" ...attributes>
    <div class="flex items-start min-w-[1440px]">
      {{! ─────────────────────────── Navigation Bar ─────────────────────────── }}
      <div
        class="bg-[#0d0d0d] border-r border-white/20 flex flex-col items-center justify-between shrink-0 w-[240px] min-h-screen [box-shadow:2px_2px_4px_black]"
      >
        <div class="flex flex-col items-center px-3 w-full">
          <div class="flex items-center px-3 py-6 w-full">
            <div class="flex gap-1.5 items-center">
              <img
                src="/assets/images/brand-2026/logo-mark.svg"
                alt=""
                class="block size-10"
              />
              <img
                src="/assets/images/brand-2026/logo-wordmark.svg"
                alt=""
                class="block h-10 w-[102px]"
              />
            </div>
          </div>

          <div class="flex flex-col gap-8 items-start w-full">
            <div class="flex flex-col items-start w-full">
              <a
                href="#"
                class="bg-[#241538] flex gap-2 items-center p-3 rounded-xl w-full"
              >
                <PhHouse @size={{20}} class="text-[#d3b6f0] shrink-0" />
                <span class="font-['Montserrat'] font-semibold text-[#d3b6f0] text-base">Home</span>
              </a>
            </div>

            <div class="flex flex-col items-start w-full">
              <div class="pb-2 px-2.5 w-full">
                <span
                  class="font-['Montserrat'] font-semibold text-[#a3a3a3] text-sm tracking-[0.25px]"
                >Benefits</span>
              </div>
              <div class="flex flex-col items-start w-full">
                <a href="#" class="flex gap-2 items-center p-3 rounded-md w-full">
                  <PhGraduationCap @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Tuition
                    Assistance</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhFirstAidKit @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Loan
                    Wellness</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhLifebuoy @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Loan
                    Forgiveness</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhHandCoins @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Loan
                    Repayment</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhGlobe @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Learning
                    Partners</span>
                </a>
              </div>
            </div>

            <div class="flex flex-col items-start w-full">
              <div class="pb-2 px-2.5 w-full">
                <span
                  class="font-['Montserrat'] font-semibold text-[#a3a3a3] text-sm tracking-[0.25px]"
                >Resources</span>
              </div>
              <div class="flex flex-col items-start w-full">
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhMegaphone @size={{20}} class="-scale-x-100 text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Coaching</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhBookOpenText @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">FAQs</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhHeadset @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Support</span>
                </a>
                <a href="#" class="flex gap-2 items-center p-3 w-full">
                  <PhQuestionMark @size={{20}} class="text-white shrink-0" />
                  <span class="font-['Montserrat'] font-medium text-white text-base">Help</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center p-6 w-full">
          <div class="flex flex-1 items-center">
            <div
              class="bg-white/20 flex gap-1 items-center pl-1.5 pr-3 py-1.5 rounded-full"
            >
              <div
                class="bg-[#d3b6f0] flex flex-col items-center justify-center rounded-[14px] size-6"
              >
                <span class="font-['Montserrat'] font-medium text-black text-xs leading-[0.9]">MS</span>
              </div>
              <span class="font-['Montserrat'] font-medium text-white text-base leading-[0.9]">Mark
                Smith</span>
            </div>
          </div>
        </div>
      </div>

      {{! ─────────────────────────── Scrollable content ─────────────────────────── }}
      <div class="flex flex-col flex-1 min-w-0">

        {{! ─── Hero ─── }}
        <DataIndicator
          @source="placeholder"
          @dataSourceId="brand-2026-hero-kpis"
          class="flex flex-col items-start p-10 rounded-2xl w-full"
        >
          <div
            class="bg-[#241538] flex flex-col gap-10 items-start overflow-clip p-6 rounded-3xl w-full"
          >
            <div class="flex items-start justify-between w-full">
              <div class="flex flex-col gap-2 items-start">
                <div class="flex gap-2 items-center">
                  <PhSun @size={{20}} class="text-white/72" />
                  <span
                    class="font-['Montserrat'] font-medium text-white/72 text-base"
                  >Good morning, Mark</span>
                </div>
                <h1 class="font-['Montserrat'] font-semibold leading-[1.1] text-white text-2xl">
                  Your Education Benefits Dashboard
                </h1>
              </div>
              <button
                type="button"
                class="bg-[#d3b6f0] flex items-center justify-center min-h-8 px-5 py-2 rounded-xl
                  cursor-pointer transition-colors hover:bg-[#c19fe0] active:bg-[#b088d8]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d3b6f0]
                  focus-visible:ring-offset-2 focus-visible:ring-offset-[#241538]"
              >
                <span class="font-['Montserrat'] font-semibold text-black text-base">View
                  Benefits</span>
              </button>
            </div>

            <div class="flex gap-6 items-start justify-center w-full">
              <div class="bg-[#0f0f0f] flex flex-1 flex-col gap-6 items-start p-4 rounded-2xl min-w-0">
                <div class="flex flex-col gap-3 items-start whitespace-nowrap">
                  <span class="font-['Montserrat'] font-medium text-white/72 text-base">Total
                    Employer Investment</span>
                  <span class="font-['Montserrat'] font-semibold text-white text-2xl">$7,270</span>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">$2,020 year
                    to date</span>
                  <PhHandshake @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>

              <div class="bg-[#0f0f0f] flex flex-1 flex-col gap-6 items-start p-4 rounded-2xl min-w-0">
                <div class="flex flex-col gap-3 items-start whitespace-nowrap">
                  <span class="font-['Montserrat'] font-medium text-white/72 text-base">Total
                    Tuition Assistance</span>
                  <span class="font-['Montserrat'] font-semibold text-white text-2xl">$5,670</span>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">$1,620 year
                    to date</span>
                  <PhGraduationCap @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>

              <div class="bg-[#0f0f0f] flex flex-1 flex-col gap-6 items-start p-4 rounded-2xl min-w-0">
                <div class="flex flex-col gap-3 items-start whitespace-nowrap">
                  <span class="font-['Montserrat'] font-medium text-white/72 text-base">Loan
                    Forgiveness Progress</span>
                  <span class="font-['Montserrat'] font-semibold text-white text-2xl">60%</span>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">72/120
                    payments made</span>
                  <PhTrendUp @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>

              <div class="bg-[#0f0f0f] flex flex-1 flex-col gap-6 items-start p-4 rounded-2xl min-w-0">
                <div class="flex flex-col gap-3 items-start whitespace-nowrap">
                  <span class="font-['Montserrat'] font-medium text-white/72 text-base">Active
                    Benefit Programs</span>
                  <span class="font-['Montserrat'] font-semibold text-white text-2xl">2</span>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">currently in
                    progress</span>
                  <PhSpinnerGap @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </DataIndicator>

        {{! ─── Needs Your Attention ─── }}
        <DataIndicator
          @source="placeholder"
          @dataSourceId="brand-2026-needs-attention"
          class="flex flex-col items-start px-10 py-8 w-full"
        >
          <div class="bg-white/[0.06] flex flex-col gap-4 items-start p-4 rounded-2xl w-full">
            <div class="flex gap-1 items-center">
              <h2 class="font-['Montserrat'] font-semibold leading-[1.4] text-lg text-white">Needs
                Your Attention</h2>
              <span
                class="font-['Montserrat'] font-medium text-white/72 text-base"
              >({{NEEDS_ATTENTION.length}})</span>
            </div>

            <div class="flex flex-col gap-2 items-start rounded-xl w-full">
              {{#each NEEDS_ATTENTION as |task|}}
                <div
                  class="bg-[#0f0f0f] border border-white/20 flex flex-col items-start p-4 rounded-2xl w-full [box-shadow:2px_2px_4px_black]"
                >
                  <div class="flex items-center justify-between w-full">
                    <div class="flex gap-3 items-center">
                      <div
                        class="{{task.iconBg}} flex flex-col items-center justify-center rounded-lg size-12 shrink-0"
                      >
                        <task.icon @size={{32}} class="text-black" />
                      </div>
                      <div class="flex flex-col gap-1 items-start">
                        <div class="flex gap-2 items-center">
                          <span
                            class="font-['Montserrat'] font-semibold text-base text-white tracking-[0.1px]"
                          >{{task.title}}</span>
                          <span
                            class="{{task.chipBg}} {{task.chipText}} flex h-4 items-center justify-center px-2 py-1 rounded-[7px] font-['Montserrat'] font-medium text-[10px]"
                          >{{task.chipLabel}}</span>
                        </div>
                        <span
                          class="font-['Montserrat'] font-medium leading-[1.6] text-sm text-white/72"
                        >{{task.description}}</span>
                      </div>
                    </div>
                    <a href="#" class="flex gap-2 items-center shrink-0">
                      <span
                        class="font-['Montserrat'] font-semibold text-[#7d9fc9] text-sm"
                      >{{task.linkLabel}}</span>
                      <PhArrowRight @size={{24}} class="text-[#7d9fc9]" />
                    </a>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        </DataIndicator>

        {{! ─── Active Benefits ─── }}
        <DataIndicator
          @source="placeholder"
          @dataSourceId="brand-2026-active-benefits"
          class="flex flex-col gap-4 items-start p-10 w-full"
        >
          <h2 class="font-['Montserrat'] font-semibold text-lg text-white">Active Benefits</h2>
          <div class="flex gap-6 items-stretch w-full">
            <div
              class="bg-[#0f0f0f] border border-white/20 flex flex-1 flex-col gap-8 items-start justify-center p-4 rounded-2xl min-w-0 [box-shadow:2px_2px_4px_black]"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex gap-1.5 items-start">
                  <PhGraduationCap @size={{16}} class="text-white shrink-0" />
                  <span
                    class="font-['Montserrat'] font-semibold text-base text-white tracking-[0.1px]"
                  >Tuition Assistance</span>
                </div>
                <a href="#" class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-semibold text-[#7d9fc9] text-base">View
                    activity</span>
                  <PhArrowRight @size={{20}} class="text-[#7d9fc9]" />
                </a>
              </div>
              <div class="flex flex-col gap-4 items-start p-4 rounded-2xl w-full">
                <div class="flex flex-col gap-4 items-start w-full">
                  <div class="flex flex-col gap-2 items-start">
                    <span class="font-['Montserrat'] font-semibold text-white text-[28px]">$1,620</span>
                    <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-base">Used This
                      Benefit Year</span>
                  </div>
                  <div class="bg-white/20 flex items-start rounded-2xl w-full">
                    <div class="bg-[#d3b6f0] h-3 rounded-2xl" style="width: 40%"></div>
                  </div>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">eligible up
                    to $4,050</span>
                  <PhGift @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>
            </div>

            <div
              class="bg-[#0f0f0f] border border-white/20 flex flex-1 flex-col gap-8 items-start justify-center p-4 rounded-2xl min-w-0 [box-shadow:2px_2px_4px_black]"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex gap-1.5 items-start">
                  <PhShieldCheck @size={{16}} class="text-white shrink-0" />
                  <span
                    class="font-['Montserrat'] font-semibold text-base text-white tracking-[0.1px]"
                  >Loan Forgiveness (PSLF)</span>
                </div>
                <a href="#" class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-semibold text-[#7d9fc9] text-base">View
                    progress</span>
                  <PhArrowRight @size={{20}} class="text-[#7d9fc9]" />
                </a>
              </div>
              <div class="flex flex-col gap-4 items-start p-4 rounded-2xl w-full">
                <div class="flex flex-col gap-4 items-start w-full">
                  <div class="flex flex-col gap-2 items-start">
                    <span class="font-['Montserrat'] font-semibold text-white text-[28px]">60%</span>
                    <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-base">Loan
                      Forgiveness Progress</span>
                  </div>
                  <div class="bg-white/20 flex items-start rounded-2xl w-full">
                    <div class="bg-[#d3b6f0] h-3 rounded-2xl" style="width: 60%"></div>
                  </div>
                </div>
                <div class="flex gap-1.5 items-center">
                  <span class="font-['Montserrat'] font-medium text-[#a3a3a3] text-sm">72/120
                    payments made</span>
                  <PhTrendUp @size={{16}} class="text-[#a3a3a3] shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </DataIndicator>

        {{! ─── More to Explore ─── }}
        <div class="flex flex-col gap-4 items-start p-10 w-full">
          <h2 class="font-['Montserrat'] font-semibold text-lg text-white">More to Explore</h2>
          <div class="flex gap-6 items-stretch w-full">
            {{#each MORE_TO_EXPLORE as |card|}}
              <div
                class="bg-[#0f0f0f] border border-white/10 flex flex-1 flex-col gap-3 items-start p-4 rounded-2xl min-w-0 [box-shadow:2px_2px_4px_black]"
              >
                <div
                  class="bg-[#474747] flex flex-col items-center justify-center rounded-lg size-12"
                >
                  <card.icon @size={{40}} class="text-white" />
                </div>
                <div class="flex flex-col gap-1 items-start w-full">
                  <span
                    class="font-['Montserrat'] font-semibold leading-[1.4] text-base text-white tracking-[0.1px]"
                  >{{card.title}}</span>
                  <p class="font-['Montserrat'] font-medium leading-[1.6] text-sm text-white/88">
                    {{card.description}}
                  </p>
                </div>
                <span
                  class="font-['Montserrat'] font-semibold leading-[1.6] text-sm text-white/72 tracking-[0.5px]"
                >{{card.tag}}</span>
              </div>
            {{/each}}
          </div>
        </div>

        {{! ─── Resources ─── }}
        <div class="flex flex-col gap-4 items-start px-10 pt-10 pb-20 w-full">
          <h2 class="font-['Montserrat'] font-semibold text-lg text-white">Resources</h2>
          <div class="flex gap-6 items-stretch w-full">
            {{#each RESOURCES as |card|}}
              <div
                class="bg-[#0f0f0f] border border-white/20 flex flex-1 flex-col gap-3 items-start p-4 rounded-2xl min-w-0 [box-shadow:2px_2px_4px_black]"
              >
                <div
                  class="bg-[#474747] flex flex-col items-center justify-center rounded-lg size-12"
                >
                  <card.icon @size={{40}} class="text-white" />
                </div>
                <div class="flex flex-col gap-1 items-start w-full">
                  <span
                    class="font-['Montserrat'] font-semibold leading-[1.4] text-base text-white tracking-[0.1px]"
                  >{{card.title}}</span>
                  <p class="font-['Montserrat'] font-medium leading-[1.6] text-sm text-white/88">
                    {{card.description}}
                  </p>
                </div>
              </div>
            {{/each}}
          </div>
        </div>

        {{! ─── Footer ─── }}
        <div class="bg-[#241538] flex flex-col items-start px-16 py-10 w-full">
          <div class="flex flex-col gap-14 items-start w-full">
            <div class="flex items-start justify-between w-full">
              <div class="flex flex-col gap-6 items-start w-[421px]">
                <div class="flex gap-1.5 items-end">
                  <span class="font-['Montserrat'] font-medium leading-[0.78] text-[#bfbfbf] text-lg">Powered
                    by</span>
                  <img
                    src="/assets/images/brand-2026/powered-by-tuition-io.svg"
                    alt="Tuition.io"
                    class="block h-[18px] w-[97.5px]"
                  />
                </div>
                <p class="font-['Montserrat'] font-medium leading-[1.6] text-sm text-white">
                  Partner Tagline can be configurable by partner.
                </p>
                <p class="font-['Montserrat'] font-medium text-white/72 text-xs">©2026 Tuition.io</p>
              </div>

              <div class="flex gap-6 items-center">
                <div class="flex flex-col gap-6 items-start w-[199px]">
                  <span
                    class="font-['Montserrat'] font-semibold leading-[1.1] text-base text-white tracking-[0.1px]"
                  >Contact</span>
                  <div class="flex flex-col font-['Montserrat'] font-medium gap-3 items-start leading-none text-sm text-white/72">
                    <span>support</span>
                    <span>help</span>
                  </div>
                </div>
                <div class="flex flex-col gap-6 items-start w-[198px]">
                  <span
                    class="font-['Montserrat'] font-semibold leading-[1.1] text-base text-white tracking-[0.1px]"
                  >Legal</span>
                  <div class="flex flex-col font-['Montserrat'] font-medium gap-3 items-start leading-none text-sm text-white/72">
                    <span>privacy policy</span>
                    <span>terms of service</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t border-white/20 flex items-start pt-10 w-full">
              <p class="font-['Montserrat'] font-medium text-white text-xs">
                CourseCa$h™ Visa® Commercial cards are powered by Stripe and issued by Celtic Bank.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>;

export default HomeTwentyTwentySix;
